import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "h-full w-full overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/50 flex flex-col gap-6" }
const _hoisted_2 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_3 = { class: "font-semibold text-lg border-b pb-2 flex items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_5 = { class: "grid grid-cols-1 md:grid-cols-2 gap-3" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "flex flex-col gap-1 pr-6" }
const _hoisted_8 = ["onUpdate:modelValue"]
const _hoisted_9 = { class: "flex flex-col gap-1" }
const _hoisted_10 = ["onUpdate:modelValue"]
const _hoisted_11 = { class: "bg-card border rounded-lg p-4 shadow-sm flex flex-col gap-4" }
const _hoisted_12 = { class: "font-semibold text-lg border-b pb-2 flex items-center gap-2" }
const _hoisted_13 = {
  key: 0,
  class: "text-center text-muted-foreground py-4 text-xs border border-dashed rounded"
}
const _hoisted_14 = { class: "flex flex-col gap-2" }
const _hoisted_15 = { class: "flex flex-col gap-1 min-w-[120px] flex-1" }
const _hoisted_16 = ["onUpdate:modelValue"]
const _hoisted_17 = ["value"]
const _hoisted_18 = { class: "flex items-center justify-center shrink-0 pt-4" }
const _hoisted_19 = { class: "flex flex-col gap-1 min-w-[120px] flex-1" }
const _hoisted_20 = ["onUpdate:modelValue"]
const _hoisted_21 = ["value"]
const _hoisted_22 = { class: "flex flex-col gap-1 min-w-[150px] flex-2" }
const _hoisted_23 = ["onUpdate:modelValue"]
const _hoisted_24 = { class: "flex items-center justify-center shrink-0 pt-4" }
const _hoisted_25 = ["onClick"]
const _hoisted_26 = { class: "text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0" }

import { ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Activity, Plus, Trash2, RefreshCw, CircleDot, ArrowRight } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

interface SDState {
  id: string
  name: string
}

interface SDTransition {
  from: string
  to: string
  text: string
}

// Estados visuales

export default /*@__PURE__*/_defineComponent({
  __name: 'StateEditorDialog',
  props: {
    open: { type: Boolean }
  },
  emits: ["update:open", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const showCode = ref(true)
const copied = ref(false)
const editableCode = ref('')

// Interfaces
const states = ref<SDState[]>([
  { id: 'EnEspera', name: 'Esperando Pago' },
  { id: 'Procesando', name: 'Procesando Orden' },
  { id: 'Completado', name: 'Orden Completada' }
])

const transitions = ref<SDTransition[]>([
  { from: '[*]', to: 'EnEspera', text: 'Iniciar flujo' },
  { from: 'EnEspera', to: 'Procesando', text: 'Pago recibido' },
  { from: 'Procesando', to: 'Completado', text: 'Éxito' },
  { from: 'Completado', to: '[*]', text: '' }
])

// Acciones Estados
const addState = () => {
  const newId = `Estado_${states.value.length + 1}`
  states.value.push({ id: newId, name: `Nuevo Estado ${states.value.length + 1}` })
}
const removeState = (idx: number) => {
  states.value.splice(idx, 1)
}

// Acciones Transiciones
const addTransition = () => {
  const from = states.value.length > 0 ? states.value[0].id : '[*]'
  const to = states.value.length > 1 ? states.value[1].id : '[*]'
  transitions.value.push({ from, to, text: 'Nueva Transición' })
}
const removeTransition = (idx: number) => {
  transitions.value.splice(idx, 1)
}

// Generar Mermaid
const generateMermaid = () => {
  let code = `stateDiagram-v2\n`
  
  // Declarar estados (excepto [*] que es implícito)
  states.value.forEach(s => {
    const safeId = s.id.trim().replace(/[^a-zA-Z0-9_]/g, '')
    if (safeId) {
      if (s.name.trim()) {
        code += `    state "${s.name.trim().replace(/"/g, '')}" as ${safeId}\n`
      } else {
        code += `    state ${safeId}\n`
      }
    }
  })

  if (states.value.length > 0) code += `\n`

  // Declarar transiciones
  transitions.value.forEach(t => {
    let from = t.from.trim()
    let to = t.to.trim()
    
    if (from !== '[*]') from = from.replace(/[^a-zA-Z0-9_]/g, '')
    if (to !== '[*]') to = to.replace(/[^a-zA-Z0-9_]/g, '')

    if (from && to) {
      if (t.text && t.text.trim()) {
        code += `    ${from} --> ${to} : ${t.text.trim()}\n`
      } else {
        code += `    ${from} --> ${to}\n`
      }
    }
  })
  
  editableCode.value = code
}

// Sincronizar desde Código a Visual
const syncToVisual = () => {
  const code = editableCode.value
  const lines = code.split('\n')
  
  const newStates: SDState[] = []
  const newTrans: SDTransition[] = []
  
  const stateRegex1 = /^state\s+"([^"]+)"\s+as\s+([a-zA-Z0-9_]+)/
  const stateRegex2 = /^state\s+([a-zA-Z0-9_]+)/
  const transRegex1 = /^(\[\*\]|[a-zA-Z0-9_]+)\s*-->\s*(\[\*\]|[a-zA-Z0-9_]+)\s*:\s*(.+)$/
  const transRegex2 = /^(\[\*\]|[a-zA-Z0-9_]+)\s*-->\s*(\[\*\]|[a-zA-Z0-9_]+)/

  const inferredStates = new Set<string>()

  for (const line of lines) {
    const t = line.trim()
    if (!t || t.startsWith('stateDiagram')) continue
    
    if (stateRegex1.test(t)) {
      const match = t.match(stateRegex1)!
      newStates.push({ name: match[1], id: match[2] })
      inferredStates.add(match[2])
    } else if (stateRegex2.test(t)) {
      const match = t.match(stateRegex2)!
      newStates.push({ name: '', id: match[1] })
      inferredStates.add(match[1])
    } else if (transRegex1.test(t)) {
      const match = t.match(transRegex1)!
      newTrans.push({ from: match[1], to: match[2], text: match[3] })
      
      if (match[1] !== '[*]' && !inferredStates.has(match[1])) {
        inferredStates.add(match[1])
        newStates.push({ id: match[1], name: '' })
      }
      if (match[2] !== '[*]' && !inferredStates.has(match[2])) {
        inferredStates.add(match[2])
        newStates.push({ id: match[2], name: '' })
      }
    } else if (transRegex2.test(t)) {
      const match = t.match(transRegex2)!
      newTrans.push({ from: match[1], to: match[2], text: '' })
      
      if (match[1] !== '[*]' && !inferredStates.has(match[1])) {
        inferredStates.add(match[1])
        newStates.push({ id: match[1], name: '' })
      }
      if (match[2] !== '[*]' && !inferredStates.has(match[2])) {
        inferredStates.add(match[2])
        newStates.push({ id: match[2], name: '' })
      }
    }
  }

  // Actualizar solo si hay cambios reales para no borrar el estado inicial si el código está mal
  if (newStates.length > 0 || newTrans.length > 0) {
    states.value = newStates
    transitions.value = newTrans
  }
}

// Watchers
watch(() => props.open, (newVal) => {
  if (newVal) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (newVal && !editableCode.value) {
    generateMermaid()
  }
}, { immediate: true })

watch([states, transitions], () => {
  generateMermaid()
}, { deep: true })

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => copied.value = false, 2000)
  } catch (err) {
    console.error('Failed to copy text: ', err)
  }
}

const saveAndClose = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseVisualEditor, {
    open: __props.open,
    "onUpdate:open": _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('update:open', $event))),
    title: "Diagrama de Estados (State Diagram)",
    description: "Diseña máquinas de estado finitas para sistemas reactivos o flujos de vida de un objeto.",
    showCode: showCode.value,
    "onUpdate:showCode": _cache[2] || (_cache[2] = ($event: any) => (showCode.value = $event)),
    copied: copied.value,
    onCopy: copyToClipboard,
    onSave: saveAndClose
  }, {
    icon: _withCtx(() => [
      _createVNode(_unref(Activity), { class: "w-5 h-5 text-primary" })
    ]),
    toolbar: _withCtx(() => [
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "default",
        onClick: addState
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(CircleDot), { class: "w-4 h-4 mr-2" }),
          _cache[3] || (_cache[3] = _createTextVNode(" Añadir Estado", -1))
        ]),
        _: 1
      }),
      _createVNode(_unref(Button), {
        size: "sm",
        variant: "secondary",
        onClick: addTransition
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(ArrowRight), { class: "w-4 h-4 mr-2" }),
          _cache[4] || (_cache[4] = _createTextVNode(" Añadir Transición", -1))
        ]),
        _: 1
      })
    ]),
    canvas: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("h3", _hoisted_3, [
            _createVNode(_unref(CircleDot), { class: "w-5 h-5 text-indigo-500" }),
            _cache[5] || (_cache[5] = _createTextVNode(" Definición de Estados ", -1))
          ]),
          (states.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, " Sin estados definidos (Nota: El estado inicial/final [*] siempre existe). "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, (st, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: 'st'+idx,
                class: "flex flex-col gap-2 bg-muted/40 border p-3 rounded-md relative group"
              }, [
                _createElementVNode("button", {
                  onClick: ($event: any) => (removeState(idx)),
                  class: "absolute right-2 top-2 text-muted-foreground hover:bg-destructive hover:text-white transition-colors p-1 rounded opacity-0 group-hover:opacity-100",
                  title: "Eliminar Estado"
                }, [
                  _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                ], 8, _hoisted_6),
                _createElementVNode("div", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "ID Único (Sin espacios)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((st.id) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none font-mono",
                    placeholder: "Ej. Procesando"
                  }, null, 8, _hoisted_8), [
                    [_vModelText, st.id]
                  ])
                ]),
                _createElementVNode("div", _hoisted_9, [
                  _cache[7] || (_cache[7] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Nombre Visual / Descripción (Opcional)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((st.name) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm focus:ring-1 focus:outline-none",
                    placeholder: "Ej. Verificando Pago..."
                  }, null, 8, _hoisted_10), [
                    [_vModelText, st.name]
                  ])
                ])
              ]))
            }), 128))
          ])
        ]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("h3", _hoisted_12, [
            _createVNode(_unref(ArrowRight), { class: "w-5 h-5 text-emerald-500" }),
            _cache[8] || (_cache[8] = _createTextVNode(" Flujo de Transiciones ", -1))
          ]),
          (transitions.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, " No hay transiciones. El diagrama estará vacío. "))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_14, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(transitions.value, (t, idx) => {
              return (_openBlock(), _createElementBlock("div", {
                key: 'tr'+idx,
                class: "flex flex-wrap items-center gap-2 bg-background border p-2 rounded-md shadow-sm"
              }, [
                _createElementVNode("div", _hoisted_15, [
                  _cache[10] || (_cache[10] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Origen (Desde)", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": ($event: any) => ((t.from) = $event),
                    class: "bg-muted border rounded px-2 py-1 text-sm focus:outline-none"
                  }, [
                    _cache[9] || (_cache[9] = _createElementVNode("option", { value: "[*]" }, "[*] Inicial / Final", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, (s) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: 'from_'+s.id,
                        value: s.id
                      }, _toDisplayString(s.id), 9, _hoisted_17))
                    }), 128))
                  ], 8, _hoisted_16), [
                    [_vModelSelect, t.from]
                  ])
                ]),
                _createElementVNode("div", _hoisted_18, [
                  _createVNode(_unref(ArrowRight), { class: "w-4 h-4 text-muted-foreground" })
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _cache[12] || (_cache[12] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Destino (Hacia)", -1)),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": ($event: any) => ((t.to) = $event),
                    class: "bg-muted border rounded px-2 py-1 text-sm focus:outline-none"
                  }, [
                    _cache[11] || (_cache[11] = _createElementVNode("option", { value: "[*]" }, "[*] Inicial / Final", -1)),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(states.value, (s) => {
                      return (_openBlock(), _createElementBlock("option", {
                        key: 'to_'+s.id,
                        value: s.id
                      }, _toDisplayString(s.id), 9, _hoisted_21))
                    }), 128))
                  ], 8, _hoisted_20), [
                    [_vModelSelect, t.to]
                  ])
                ]),
                _createElementVNode("div", _hoisted_22, [
                  _cache[13] || (_cache[13] = _createElementVNode("label", { class: "text-[10px] font-bold uppercase text-muted-foreground ml-1" }, "Condición / Evento (Opcional)", -1)),
                  _withDirectives(_createElementVNode("input", {
                    "onUpdate:modelValue": ($event: any) => ((t.text) = $event),
                    class: "bg-background border rounded px-2 py-1 text-sm focus:outline-none focus:ring-1",
                    placeholder: "Ej. click_button"
                  }, null, 8, _hoisted_23), [
                    [_vModelText, t.text]
                  ])
                ]),
                _createElementVNode("div", _hoisted_24, [
                  _createElementVNode("button", {
                    onClick: ($event: any) => (removeTransition(idx)),
                    class: "text-muted-foreground hover:bg-destructive hover:text-white p-1 rounded transition-colors"
                  }, [
                    _createVNode(_unref(Trash2), { class: "w-4 h-4" })
                  ], 8, _hoisted_25)
                ])
              ]))
            }), 128))
          ])
        ])
      ])
    ]),
    "code-panel": _withCtx(() => [
      _createElementVNode("div", _hoisted_26, [
        _cache[15] || (_cache[15] = _createTextVNode(" Mermaid (Sincronizado) ", -1)),
        _createVNode(_unref(Button), {
          size: "sm",
          variant: "default",
          class: "h-7 text-xs",
          onClick: syncToVisual
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(RefreshCw), { class: "w-3 h-3 mr-2" }),
            _cache[14] || (_cache[14] = _createTextVNode(" Sincronizar a Visual ", -1))
          ]),
          _: 1
        })
      ]),
      _createVNode(MermaidCodeEditor, {
        modelValue: editableCode.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((editableCode).value = $event)),
        class: "flex-1 min-h-0"
      }, null, 8, ["modelValue"])
    ]),
    "live-preview": _withCtx(() => [
      _createVNode(MermaidLivePreview, { code: editableCode.value }, null, 8, ["code"])
    ]),
    _: 1
  }, 8, ["open", "showCode", "copied"]))
}
}

})